import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText, translateTextArray } from '../utils/translateText';

/**
 * Component to display translated user-entered text
 * Automatically translates when language changes
 */
export const TranslatedText = ({ text, className, style, title }) => {
  const { i18n } = useTranslation();
  const [translated, setTranslated] = useState(text);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const translate = async () => {
      if (!text || (typeof text === 'string' && text.trim() === '')) {
        setTranslated(text);
        return;
      }

      setLoading(true);
      try {
        const translatedText = await translateText(text, i18n.language);
        setTranslated(translatedText);
      } catch (error) {
        console.error('Translation error:', error);
        setTranslated(text); // Fallback to original
      } finally {
        setLoading(false);
      }
    };

    translate();
  }, [text, i18n.language]);

  if (!text) return null;

  return (
    <span className={className} style={style} title={title || text}>
      {loading ? text : translated}
    </span>
  );
};

/**
 * Component to display translated array of texts (e.g., symptoms, triggers)
 */
export const TranslatedTextArray = ({ texts, separator = ', ', className, style }) => {
  const { i18n } = useTranslation();
  const [translated, setTranslated] = useState(texts);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const translate = async () => {
      if (!Array.isArray(texts) || texts.length === 0) {
        setTranslated(texts || []);
        return;
      }

      setLoading(true);
      try {
        const translatedArray = await translateTextArray(texts, i18n.language);
        setTranslated(translatedArray);
      } catch (error) {
        console.error('Translation error:', error);
        setTranslated(texts); // Fallback to original
      } finally {
        setLoading(false);
      }
    };

    translate();
  }, [texts, i18n.language]);

  if (!Array.isArray(texts) || texts.length === 0) return null;

  return (
    <span className={className} style={style}>
      {loading ? texts.join(separator) : translated.join(separator)}
    </span>
  );
};

export default TranslatedText;

